import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "@/lib/utils.js"

/**
 * The explanation behind a label, for the numbers that need one.
 *
 * Printing every explanation turned the health screen into a wall of prose,
 * which is the same failure as the wall of figures it replaced. The label
 * carries a dotted underline so it is visibly askable, and the sentence is one
 * hover — or one tab stop — away.
 *
 * Radix rather than `title`: a `title` cannot be reached by keyboard, cannot be
 * styled, and appears after a delay nobody can predict.
 */
export const TooltipProvider = TooltipPrimitive.Provider

export function Tooltip({
  children,
  content,
  ...props
}: Omit<ComponentProps<typeof TooltipPrimitive.Root>, "children"> & {
  children: ReactNode
  content: ReactNode
}) {
  return (
    <TooltipPrimitive.Root {...props}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            "z-50 max-w-xs rounded-md border bg-popover px-3 py-2 text-[13px] leading-snug text-foreground shadow-md",
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

/** A label that says, by its dotted rule, that there is more behind it. */
export function Explained({ hint, children }: { hint: string | undefined; children: ReactNode }) {
  if (hint === undefined) return <>{children}</>
  return (
    <Tooltip content={hint}>
      <button
        type="button"
        className="cursor-help text-left underline decoration-border decoration-dotted underline-offset-4"
      >
        {children}
      </button>
    </Tooltip>
  )
}
